import { useRef } from "react";
import {
  ArrowDown,
  ArrowRight,
  CalendarDays,
  Compass,
  MountainSnow,
  Radio,
  ShieldCheck,
  ThermometerSnowflake,
  Wind,
} from "lucide-react";
import { ConceptBar } from "../components/ConceptBar";
import { DemoForm } from "../components/DemoForm";
import { MetricValue } from "../components/MetricValue";
import { ReactiveGrid } from "../components/ReactiveGrid";
import { useGsap } from "../hooks/useGsap";
import "../styles/nord.css";

const chapters = [
  {
    index: "01",
    eyebrow: "Маршрут",
    title: "Там, где карта заканчивается.",
    copy: "18 дней вдоль северного берега Шпицбергена: ледовые бухты, полевые станции и высадки, доступные только с воды.",
    meta: "1 140 км / 7 высадок",
    image: "/assets/nord/camp.webp",
    alt: "Полевой лагерь экспедиции под северным сиянием",
  },
  {
    index: "02",
    eyebrow: "Исследование",
    title: "Собирать данные, а не впечатления.",
    copy: "Участники работают рядом с гляциологами и морскими биологами: ведут фотограмметрию льда, отбирают пробы и фиксируют акустическую активность китов.",
    meta: "3 научные программы",
    image: "/assets/nord/scientist.webp",
    alt: "Гляциолог исследует ледяной керн",
  },
  {
    index: "03",
    eyebrow: "Команда",
    title: "Двенадцать мест. Ни одного пассажира.",
    copy: "Каждый проходит подготовку, получает полевую роль и отвечает за часть общей миссии. Опыт экспедиций желателен, но не обязателен.",
    meta: "12 участников / 6 экспертов",
    image: "/assets/nord/fieldwork.webp",
    alt: "Команда работает у ледовой кромки",
  },
  {
    index: "04",
    eyebrow: "Результат",
    title: "Вернуться с доказательствами перемен.",
    copy: "После миссии команда выпускает открытый атлас наблюдений, короткий документальный фильм и набор данных для партнёрских лабораторий.",
    meta: "Open data / film / field notes",
    image: "/assets/nord/hero.webp",
    alt: "Исследователь на фоне арктического ледника",
  },
];

export function NordPage() {
  const page = useRef<HTMLElement>(null);

  useGsap(page, (gsap) => {
    gsap
      .timeline({ defaults: { ease: "power4.out" } })
      .from(".nord-hero__eyebrow, .nord-hero__actions, .nord-hero__aside", {
        opacity: 0,
        y: 28,
        duration: 1,
        stagger: 0.08,
      })
      .from(
        ".nord-hero__title span",
        { yPercent: 115, rotate: 2, duration: 1.2, stagger: 0.08 },
        0.08,
      )
      .from(".nord-hero__frame", { clipPath: "inset(12% 12% 12% 12%)", duration: 1.45 }, 0)
      .from(".nord-hero__image", { scale: 1.18, duration: 1.8 }, 0);

    gsap.to(".nord-hero__image", {
      yPercent: 12,
      scale: 1.06,
      ease: "none",
      scrollTrigger: {
        trigger: ".nord-hero",
        start: "top top",
        end: "bottom top",
        scrub: true,
      },
    });

    gsap.to(".nord-progress__bar", {
      scaleX: 1,
      ease: "none",
      scrollTrigger: {
        trigger: page.current,
        start: "top top",
        end: "bottom bottom",
        scrub: 0.15,
      },
    });

    gsap.utils.toArray<HTMLElement>(".nord-route__chapter").forEach((chapter) => {
      const visual = chapter.querySelector(".nord-route__visual");
      const copy = chapter.querySelector(".nord-route__copy");
      gsap.from(visual, {
        clipPath: "inset(8% 8% 8% 8%)",
        scale: 0.94,
        duration: 1.05,
        ease: "power4.out",
        scrollTrigger: { trigger: chapter, start: "top 78%" },
      });
      gsap.from(copy, {
        y: 54,
        opacity: 0,
        duration: 0.85,
        ease: "power3.out",
        scrollTrigger: { trigger: chapter, start: "top 74%" },
      });
    });

    gsap.to(".nord-route__radar i", {
      rotate: 360,
      duration: 8,
      repeat: -1,
      ease: "none",
    });

    gsap.from(".nord-manifesto__line span", {
      yPercent: 110,
      duration: 1,
      stagger: 0.08,
      ease: "power4.out",
      scrollTrigger: {
        trigger: ".nord-manifesto",
        start: "top 72%",
      },
    });

    const media = gsap.matchMedia();
    media.add("(min-width: 701px)", () => {
      gsap.fromTo(
        ".nord-fieldwork__image img",
        { scale: 1.14, yPercent: -5 },
        {
          scale: 1,
          yPercent: 5,
          ease: "none",
          scrollTrigger: {
            trigger: ".nord-fieldwork",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
    });

    gsap.utils.toArray<HTMLElement>(".nord-readiness article").forEach((card, index) => {
      gsap.from(card, {
        y: 90 + index * 18,
        rotate: index % 2 ? 1.5 : -1.5,
        opacity: 0,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: { trigger: card, start: "top 88%" },
      });
    });

    gsap.from(".nord-evidence__card", {
      y: 48,
      opacity: 0,
      stagger: 0.08,
      duration: 0.7,
      ease: "power3.out",
      scrollTrigger: { trigger: ".nord-evidence", start: "top 75%" },
    });
  });

  return (
    <main id="main" ref={page} className="nord">
      <ConceptBar label="Экспедиция" dark />
      <div className="nord-progress" aria-hidden="true">
        <i className="nord-progress__bar" />
      </div>

      <header className="nord-nav">
        <a href="#top" className="nord-logo" aria-label="NORD/79 — наверх">
          NORD<span>/79</span>
        </a>
        <nav aria-label="Основная навигация">
          <a href="#mission">Миссия</a>
          <a href="#route">Маршрут</a>
          <a href="#apply">Участие</a>
        </nav>
        <a href="#apply" className="nord-nav__cta" data-magnetic>
          Экспедиция 2027 <ArrowRight aria-hidden="true" />
        </a>
      </header>

      <section className="nord-hero" id="top" aria-labelledby="nord-title">
        <ReactiveGrid className="nord-coordinate-field" color="200, 255, 54" cell={54} radius={170} strength={24} variant="crosses" />
        <div className="nord-hero__frame">
          <img
            className="nord-hero__image"
            src="/assets/nord/hero.webp"
            alt="Арктический исследователь перед ледником"
            width="1536"
            height="1024"
            fetchPriority="high"
          />
        </div>
        <div className="nord-hero__veil" />
        <div className="nord-hero__copy">
          <p className="nord-hero__eyebrow">79° северной широты · Svalbard · август 2027</p>
          <h1 id="nord-title" className="nord-hero__title">
            <span>Север</span>
            <span>не ждёт</span>
          </h1>
          <div className="nord-hero__actions">
            <p>
              Полевое путешествие для тех, кто готов наблюдать климатические изменения не через экран
            </p>
            <a href="#apply" data-magnetic>
              Подать заявку <ArrowRight aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="nord-hero__aside">
          <span>Следующая точка</span>
          <b>Ny-Ålesund / 78.923° N</b>
          <ArrowDown aria-hidden="true" />
        </div>
      </section>

      <section className="nord-signal" aria-label="Параметры экспедиции">
        <div>
          <Radio aria-hidden="true" />
          <span>Связь</span>
          <b>Iridium / ежедневно</b>
        </div>
        <div>
          <ThermometerSnowflake aria-hidden="true" />
          <span>Температура</span>
          <b>−4…+6 °C</b>
        </div>
        <div>
          <Wind aria-hidden="true" />
          <span>Условия</span>
          <b>Ветер до 22 м/с</b>
        </div>
        <p>Набор открыт до 14 февраля. Собеседования проходят по мере поступления заявок.</p>
      </section>

      <section className="nord-manifesto" id="mission">
        <p>Почему сейчас</p>
        <h2 aria-label="Арктика меняется быстрее, чем мы успеваем её описывать.">
          <span className="nord-manifesto__line">
            <span>Арктика меняется быстрее,</span>
          </span>
          <span className="nord-manifesto__line">
            <span>чем мы успеваем её описывать.</span>
          </span>
        </h2>
        <div className="nord-manifesto__note">
          <span>01—04</span>
          <p>
            Поэтому NORD/79 соединяет научную работу и экспедиционный опыт: участник не наблюдает со
            стороны, а помогает собрать материал, который останется после путешествия.
          </p>
        </div>
      </section>

      <section className="nord-metrics" aria-label="Экспедиция в цифрах">
        <article><strong><MetricValue value={1140} suffix=" км" /></strong><span>морского маршрута</span></article>
        <article><strong><MetricValue value={18} suffix=" дней" /></strong><span>полевой программы</span></article>
        <article><strong><MetricValue value={12} suffix=" мест" /></strong><span>в одной команде</span></article>
        <article><strong><MetricValue value={3} /></strong><span>научных протокола</span></article>
      </section>

      <section className="nord-route" id="route" aria-label="Сценарий экспедиции">
        <aside className="nord-route__aside">
          <p>Field log / 2027</p>
          <h2 data-split>18 дней на краю обитаемой карты.</h2>
          <div className="nord-route__radar" aria-hidden="true">
            <i />
            <Compass />
          </div>
          <span>Прокручивайте полевой журнал ↓</span>
        </aside>
        <div className="nord-route__chapters">
          {chapters.map((chapter) => (
            <article className="nord-route__chapter" key={chapter.index} data-spotlight>
              <div className="nord-route__visual" data-tilt>
                <img
                  src={chapter.image}
                  alt={chapter.alt}
                  width="1600"
                  height="1200"
                  loading="lazy"
                />
                <span>{chapter.meta}</span>
              </div>
              <div className="nord-route__copy">
                <span>{chapter.index}</span>
                <p>{chapter.eyebrow}</p>
                <h3>{chapter.title}</h3>
                <p>{chapter.copy}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="nord-fieldwork">
        <div className="nord-fieldwork__image">
          <img
            src="/assets/nord/camp.webp"
            alt="Экспедиционный лагерь на морском льду"
            width="1600"
            height="900"
            loading="lazy"
          />
          <span>Field station / day 11</span>
        </div>
        <div className="nord-fieldwork__copy">
          <p>Не туристический маршрут</p>
          <h2 data-split>Сначала — подготовка. Потом — север.</h2>
          <p>
            За шесть недель до выезда команда проходит дистанционный курс, медицинский допуск и
            двухдневный сбор в Тромсё. На маршруте работают судовой врач, два гида и руководители
            научных программ.
          </p>
          <ul>
            <li><ShieldCheck aria-hidden="true" /> Страхование и полевой комплект включены</li>
            <li><MountainSnow aria-hidden="true" /> Опыт альпинизма не требуется</li>
            <li><CalendarDays aria-hidden="true" /> 07–24 августа 2027</li>
          </ul>
        </div>
      </section>

      <section className="nord-evidence" aria-labelledby="nord-evidence-title">
        <div className="nord-evidence__portrait">
          <img
            src="/assets/nord/scientist.webp"
            alt="Руководитель гляциологической программы с ледяным керном"
            width="1200"
            height="1800"
            loading="lazy"
          />
          <span>Dr. Lina Mørk / Glaciology lead</span>
        </div>
        <div className="nord-evidence__content">
          <p>Что останется после маршрута</p>
          <h2 id="nord-evidence-title" data-split>Не сувенир. Полевое доказательство.</h2>
          <div className="nord-evidence__grid">
            <article className="nord-evidence__card" data-spotlight>
              <span>01 / ATLAS</span>
              <h3>Открытая карта ледовых разломов</h3>
              <p>Геопривязанные наблюдения команды проходят проверку и попадают в общий атлас сезона.</p>
            </article>
            <article className="nord-evidence__card" data-spotlight>
              <span>02 / FILM</span>
              <h3>24 минуты документального материала</h3>
              <p>Монтаж строится вокруг работы в поле, а не постановочных кадров героического туризма.</p>
            </article>
            <article className="nord-evidence__card" data-spotlight>
              <span>03 / DATA</span>
              <h3>18 000+ измерений</h3>
              <p>Температура, солёность, акустика и фотограмметрия передаются партнёрским лабораториям.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="nord-readiness" aria-labelledby="readiness-title">
        <div className="nord-readiness__heading">
          <p>Готовность к миссии</p>
          <h2 id="readiness-title" data-split>Что требуется от участника.</h2>
        </div>
        <article>
          <span>01</span>
          <h3>Выносливость</h3>
          <p>Спокойно проходить 8–12 км по пересечённой местности и работать на холоде несколько часов.</p>
        </article>
        <article>
          <span>02</span>
          <h3>Дисциплина</h3>
          <p>Следовать протоколам безопасности, вести полевой журнал и не выпадать из командного ритма.</p>
        </article>
        <article>
          <span>03</span>
          <h3>Любопытство</h3>
          <p>Задавать точные вопросы, осваивать новые инструменты и делиться результатом после возвращения.</p>
        </article>
      </section>

      <section className="nord-apply" id="apply">
        <div className="nord-apply__copy">
          <p>12 мест · набор открыт</p>
          <h2 data-split>Ваше место в полевом журнале ещё пустует.</h2>
          <p>
            Расскажите о себе. Координатор пришлёт программу, бюджет участия и предложит время для
            короткого знакомства.
          </p>
        </div>
        <DemoForm
          title="Начать отбор"
          description="Первый разговор занимает 20 минут и ни к чему не обязывает."
          submitLabel="Отправить заявку"
          successMessage="Заявка принята."
          tone="dark"
          fields={["name", "email"]}
        />
      </section>

      <footer className="nord-footer">
        <a href="#top" className="nord-logo">NORD<span>/79</span></a>
        <p>Arctic field programme · Longyearbyen / Tromsø</p>
        <a href="mailto:crew@nord79.example">crew@nord79.example</a>
      </footer>
    </main>
  );
}
